import { loadState, persistState } from './store.js';
import { CITIES } from './data.js';

export const bus = new EventTarget();

function emit(name, detail) {
    bus.dispatchEvent(new CustomEvent(name, { detail }));
}

class Store {
    constructor() {
        this.state = loadState();
        this.checkDailyReset();
    }

    get() {
        return this.state;
    }

    save() {
        persistState(this.state);
    }

    update(mutator) {
        mutator(this.state);
        this.save();
        emit('change', this.state);
    }

    checkDailyReset() {
        const today = new Date().toDateString();
        if (this.state.todayDate !== today) {
            this.state.todayDate = today;
            this.state.todayCount = 0;
            this.save();
        }
    }

    cityConfig(cityId) {
        return CITIES.find(c => c.id === cityId);
    }

    getCityState(cityId) {
        return this.state.cities[cityId];
    }

    activeDirectionIds() {
        return Object.keys(this.state.directions).filter(id => this.state.directions[id].active);
    }

    activeCities() {
        return CITIES.filter(c => {
            const s = this.state.cities[c.id];
            return s && (s.cityOn || s.regionOn);
        });
    }

    connectedCitiesCount() {
        return this.activeCities().length;
    }

    canReceiveLeads() {
        const s = this.state;
        if (!s.acceptingLeads) return false;
        if (!this.isWithinSchedule()) return false;
        if (this.activeDirectionIds().length === 0) return false;
        if (this.activeCities().length === 0) return false;
        if (s.todayCount >= s.dailyLimit) return false;
        if (s.balance < 300) return false;
        return true;
    }

    isWithinSchedule() {
        const sch = this.state.schedule;
        if (!sch || sch.roundTheClock) return true;
        const now = new Date();
        const [fromH, fromM] = sch.from.split(':').map(Number);
        const [toH, toM] = sch.to.split(':').map(Number);
        const minutesNow = now.getHours() * 60 + now.getMinutes();
        const fromMin = fromH * 60 + fromM;
        const toMin = toH * 60 + toM;
        if (fromMin === toMin) return true;
        if (fromMin < toMin) return minutesNow >= fromMin && minutesNow <= toMin;
        return minutesNow >= fromMin || minutesNow <= toMin;
    }

    addLead(lead) {
        this.update(state => {
            state.leads.unshift(lead);
            state.balance -= lead.cost;
            state.todayCount += 1;
            state.operations.unshift({
                id: 'op-charge-' + lead.id,
                type: 'charge',
                amount: -lead.cost,
                date: lead.createdAt,
                leadId: lead.id
            });
        });
        emit('lead:new', lead);
    }

    addNotification(notification) {
        this.update(state => {
            state.notifications.unshift({
                id: 'notif-' + Date.now() + '-' + Math.round(Math.random() * 1000),
                read: false,
                createdAt: Date.now(),
                ...notification
            });
            if (state.notifications.length > 50) state.notifications.length = 50;
        });
        emit('notification:new', notification);
    }

    markNotificationsRead() {
        this.update(state => {
            state.notifications.forEach(n => { n.read = true; });
        });
    }

    unreadNotificationsCount() {
        return this.state.notifications.filter(n => !n.read).length;
    }

    getLead(id) {
        return this.state.leads.find(l => l.id === id);
    }

    updateLead(id, patch) {
        this.update(state => {
            const lead = state.leads.find(l => l.id === id);
            if (lead) Object.assign(lead, patch);
        });
        emit('lead:updated', id);
    }

    startDispute(id, reason, comment) {
        this.update(state => {
            const lead = state.leads.find(l => l.id === id);
            if (!lead) return;
            lead.dispute = {
                reason,
                comment,
                status: 'review',
                startedAt: Date.now(),
                resolvedAt: null,
                result: null
            };
        });
        emit('lead:dispute-started', id);
    }

    resolveDispute(id, approved) {
        let lead;
        this.update(state => {
            lead = state.leads.find(l => l.id === id);
            if (!lead || !lead.dispute) return;
            lead.dispute.status = 'resolved';
            lead.dispute.resolvedAt = Date.now();
            lead.dispute.result = approved ? 'refunded' : 'rejected';
            if (approved) {
                state.balance += lead.cost;
                state.operations.unshift({
                    id: 'op-refund-' + id,
                    type: 'refund',
                    amount: lead.cost,
                    date: Date.now(),
                    leadId: id
                });
            }
        });
        this.addNotification({
            type: 'dispute',
            leadId: id,
            text: approved
                ? `Заявка №${lead.number}: брак подтверждён, ${lead.cost} ₽ возвращены на баланс`
                : `Заявка №${lead.number}: модератор связался с клиентом, запрос целевой`
        });
        emit('lead:dispute-resolved', id);
    }

    topUp(amount, method) {
        this.update(state => {
            state.balance += amount;
            state.operations.unshift({
                id: 'op-topup-' + Date.now(),
                type: 'topup',
                amount,
                date: Date.now(),
                method
            });
        });
        emit('balance:topup', amount);
    }
}

export const store = new Store();
